import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import frameStore from '@client/stores/frameStore';
import { twMerge } from 'tailwind-merge';
import { EllipsisVerticalIcon } from 'lucide-react';

const FrameTitle = observer(
    ({
        title,
        isShowOnlyHeader = false,
        isLoading = false,
        isShowMoreButton = false,
        onClickMoreButton,
    }: {
        /**
         * 스크롤 했을 때 네비게이션 바에 표시됨
         */
        title?: string;
        /**
         * 헤더만 표시하고 본문은 표시하지 않음
         */
        isShowOnlyHeader?: boolean;
        /**
         * 로딩 중일 때 표시
         */
        isLoading?: boolean;
        /**
         * 더보기 버튼 표시
         */
        isShowMoreButton?: boolean;
        /**
         * 더보기 버튼 클릭 시 호출되는 함수
         */
        onClickMoreButton?: () => void;
    }) => {
        useEffect(() => {
            frameStore.setCurrentTitle(title);

            return () => {
                frameStore.setCurrentTitle(null);
            };
        }, [title]);

        if (isLoading) {
            return (
                <header className="px-5">
                    <div className="skeleton h-6.5 w-1/2 rounded-sm" />
                </header>
            );
        }

        if (!title) {
            return null;
        }

        if (isShowOnlyHeader) {
            return null;
        }

        return (
            <header className="flex flex-row justify-between px-5">
                <h1 className="min-h-6.5 text-xl font-semibold">{title}</h1>
                {isShowMoreButton && (
                    <button
                        className="btn btn-sm btn-ghost text-base-content/70 -mr-4 h-6.5 shrink-0 grow-0"
                        onClick={(e) => {
                            e.preventDefault();
                            onClickMoreButton?.();
                        }}
                    >
                        <EllipsisVerticalIcon className="size-5" />
                    </button>
                )}
            </header>
        );
    },
);

const FrameDescription = observer(({ children }: { children?: React.ReactNode }) => {
    return <div className="text-base-content/40 -mt-3 px-5 text-sm">{children}</div>;
});

const FrameSpacer = observer(() => {
    return <div className="h-0 flex-1"></div>;
});

const FrameBottomSpacer = observer(() => {
    return <div className="min-h-20 flex-1"></div>;
});

/**
 * 프레임 컴포넌트
 *
 * - 너비 100% 를 가지고 flex col 로 gap 24px 을 가진 컴포넌트
 * - 대부분의 UI 는 Frame 하위에 잡아서 위치한다
 * - 기본 배경은 옅은 회색, 하위에 Card, Box 등으로 레이아웃을 구성한다
 * - 좌우 패딩은 하위 컴포넌트에서 처리한다
 */
const Frame = Object.assign(
    observer(({ children, className }: { children: React.ReactNode; className?: string }) => {
        return (
            <main
                className={twMerge(
                    'bg-base-200 relative flex min-h-full w-full flex-col gap-4 pt-4 pb-4',
                    className,
                )}
            >
                {children}
            </main>
        );
    }),
    {
        Title: FrameTitle,
        Description: FrameDescription,
        /**
         * 프레임의 빈 공간을 채우는 컴포넌트
         */
        Spacer: FrameSpacer,
        /**
         * 프레임 하단이 딱 붙지 않도록 스크롤 공간을 채우는 컴포넌트
         * - 뉴스처럼 문서가 있는 경우에 넣어주면 사용하기 편함
         */
        BottomSpacer: FrameBottomSpacer,
    },
);

export default Frame;
